import { useEffect, useRef, useState } from 'react';
import { X, ExternalLink, Mail, Instagram, TrendingUp, Users, ArrowRightLeft, Loader2, Check } from 'lucide-react';
import type { RisingCreator, RisingCreatorSnapshot } from '../../hooks/useRisingCreators';
import RisingScoreCircle from './RisingScoreCircle';
import StatusBadge from './StatusBadge';
import SourceBadge from './SourceBadge';
import type { RisingCreatorStatus } from '../../hooks/useRisingCreators';

const TEAM_MEMBERS = ['Hosni', 'Camille', 'Alexis'];

const STATUS_OPTIONS: { value: RisingCreatorStatus; label: string }[] = [
  { value: 'new', label: 'Nouveau' },
  { value: 'shortlisted', label: 'Présélectionné' },
  { value: 'contacted', label: 'Contacté' },
  { value: 'in_discussion', label: 'En discussion' },
  { value: 'signed', label: 'Signé' },
  { value: 'rejected', label: 'Rejeté' },
];

function formatFollowers(n: number): string {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

interface CreatorDrawerProps {
  creator: RisingCreator | null;
  snapshots: RisingCreatorSnapshot[];
  onClose: () => void;
  onStatusChange: (id: string, status: RisingCreatorStatus) => void;
  onNotesChange: (id: string, notes: string) => void;
  onAssignedToChange: (id: string, assignedTo: string) => void;
  onConvertToRecruitment: (creator: RisingCreator) => void;
}

export default function CreatorDrawer({
  creator,
  snapshots,
  onClose,
  onStatusChange,
  onNotesChange,
  onAssignedToChange,
  onConvertToRecruitment,
}: CreatorDrawerProps) {
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [savedNotes, setSavedNotes] = useState(false);
  const [converting, setConverting] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (creator) setNotes(creator.notes || '');
  }, [creator?.id]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const handleSaveNotes = async () => {
    if (!creator) return;
    setSavingNotes(true);
    await onNotesChange(creator.id, notes);
    setSavingNotes(false);
    setSavedNotes(true);
    setTimeout(() => setSavedNotes(false), 2000);
  };

  const handleConvert = async () => {
    if (!creator) return;
    setConverting(true);
    await onConvertToRecruitment(creator);
    setConverting(false);
  };

  if (!creator) return null;

  const maxFollowers = snapshots.length > 0
    ? Math.max(...snapshots.map(s => s.followers_count || 0))
    : creator.followers_count;

  return (
    <>
      <div
        ref={overlayRef}
        className="fixed inset-0 bg-black/30 z-40 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed right-0 top-0 h-full w-full max-w-xl bg-white shadow-2xl z-50 flex flex-col overflow-hidden transition-transform">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center gap-3">
            <img
              src={creator.profile_picture_url || 'https://via.placeholder.com/48'}
              alt={creator.username}
              className="w-12 h-12 rounded-full object-cover border-2 border-gray-100"
              onError={(e) => { e.currentTarget.src = 'https://via.placeholder.com/48'; }}
            />
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-base font-bold text-gray-900">{creator.full_name || creator.username}</h2>
                {creator.email && <Mail className="w-4 h-4 text-teal-500" title={creator.email} />}
              </div>
              <span className="text-sm text-gray-500">@{creator.username}</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <RisingScoreCircle score={creator.rising_score} size={44} />
            <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <div className="flex flex-wrap gap-2 items-center">
            <StatusBadge status={creator.status} />
            <SourceBadge source={creator.source} />
            {creator.profile_url && (
              <a
                href={creator.profile_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
              >
                <Instagram className="w-3.5 h-3.5" />
                Voir le profil
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <div className="flex items-center justify-center gap-1 mb-1">
                <Users className="w-3.5 h-3.5 text-gray-500" />
                <span className="text-xs text-gray-500">Followers</span>
              </div>
              <div className="font-bold text-gray-900">{formatFollowers(creator.followers_count)}</div>
              {creator.growth_rate_30d !== null && (
                <div className={`text-xs font-medium flex items-center justify-center gap-0.5 mt-0.5 ${
                  (creator.growth_rate_30d || 0) >= 0 ? 'text-green-600' : 'text-red-500'
                }`}>
                  <TrendingUp className="w-3 h-3" />
                  {(creator.growth_rate_30d || 0) >= 0 ? '+' : ''}{creator.growth_rate_30d?.toFixed(1)}% 30j
                </div>
              )}
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <span className="text-xs text-gray-500 block mb-1">Engagement</span>
              <div className={`font-bold ${
                (creator.engagement_rate || 0) >= 4 ? 'text-green-600' :
                (creator.engagement_rate || 0) >= 2 ? 'text-amber-600' : 'text-gray-900'
              }`}>
                {creator.engagement_rate?.toFixed(2) || '—'}%
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <span className="text-xs text-gray-500 block mb-1">Moy. Reels</span>
              <div className="font-bold text-gray-900">
                {creator.avg_views_reels ? formatFollowers(creator.avg_views_reels) : '—'}
              </div>
            </div>
          </div>

          {creator.bio && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bio</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{creator.bio}</p>
              {creator.email && (
                <div className="mt-2 flex items-center gap-2 text-sm text-teal-700">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${creator.email}`} className="hover:underline">{creator.email}</a>
                </div>
              )}
            </div>
          )}

          {snapshots.length > 1 && (
            <div>
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
                Evolution des followers
              </h3>
              <div className="relative h-24 bg-gray-50 rounded-xl overflow-hidden px-3 pt-2 pb-4">
                <svg width="100%" height="100%" viewBox={`0 0 ${snapshots.length * 20} 60`} preserveAspectRatio="none">
                  <polyline
                    points={snapshots
                      .map((s, i) => {
                        const x = i * 20;
                        const y = maxFollowers > 0
                          ? 60 - ((s.followers_count || 0) / maxFollowers) * 50
                          : 30;
                        return `${x},${y}`;
                      })
                      .join(' ')}
                    fill="none"
                    stroke="#2563eb"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="absolute bottom-1 left-3 right-3 flex justify-between text-[10px] text-gray-400">
                  <span>{formatDate(snapshots[0].snapshot_at)}</span>
                  <span>{formatDate(snapshots[snapshots.length - 1].snapshot_at)}</span>
                </div>
              </div>
            </div>
          )}

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Statut</h3>
            <div className="flex flex-wrap gap-2">
              {STATUS_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => onStatusChange(creator.id, opt.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    creator.status === opt.value
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Assigné à</h3>
            <div className="flex gap-2">
              {TEAM_MEMBERS.map(member => (
                <button
                  key={member}
                  onClick={() => onAssignedToChange(creator.id, member)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                    creator.assigned_to === member
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  {member}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</h3>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={4}
              placeholder="Ajouter des notes sur ce créateur..."
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            <button
              onClick={handleSaveNotes}
              disabled={savingNotes}
              className={`mt-2 flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                savedNotes
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {savingNotes ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : savedNotes ? (
                <><Check className="w-3.5 h-3.5" /> Enregistré</>
              ) : 'Enregistrer'}
            </button>
          </div>

          <div>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Informations</h3>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
              <div><span className="text-gray-400">Détecté le :</span> {formatDate(creator.first_seen_at)}</div>
              {creator.last_scraped_at && (
                <div><span className="text-gray-400">Dernier scrape :</span> {formatDate(creator.last_scraped_at)}</div>
              )}
              {creator.posts_count && (
                <div><span className="text-gray-400">Posts :</span> {creator.posts_count}</div>
              )}
              {creator.following_count && (
                <div><span className="text-gray-400">Following :</span> {formatFollowers(creator.following_count)}</div>
              )}
              {creator.social_proof_count > 0 && (
                <div className="col-span-2">
                  <span className="text-gray-400">Social proof :</span>{' '}
                  <span className="text-teal-700 font-semibold">{creator.social_proof_count} influenceur(s) signé(s) le suivent</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-white">
          <button
            onClick={handleConvert}
            disabled={converting}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors disabled:opacity-60"
          >
            {converting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <ArrowRightLeft className="w-4 h-4" />
            )}
            Convertir en lead de recrutement
          </button>
        </div>
      </div>
    </>
  );
}
